import React, { useEffect, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Tag, Row, Table, Button, Form, Spin, Col, Select, Menu, Dropdown, Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getSession } from 'app/shared/reducers/authentication';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { APP_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { getEcole } from '../ecole/ecole.reducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getEcoleApprovisonnements, soldeTotalByEcoleSalaire } from './appro.reducer';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import CountUp from 'react-countup';
import { JhiItemCount, JhiPagination, getSortState, TextFormat } from 'react-jhipster';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { ITEMS_PER_PAGE } from 'app/shared/util/pagination.constants';
import { DownloadOutlined } from '@ant-design/icons';
import { Storage } from 'react-jhipster';
import axios from 'axios';
import { useCheckIsAnneeInactive } from 'app/hooks/useCheckIsAnneeActive';

export const Approvisonnement = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, ITEMS_PER_PAGE, 'id'), props.location.search)
  );
  const totalItems = useAppSelector(state => state.approvisonnement.totalItems);
  const dispatch = useAppDispatch();
  const ecoleApprovisonnements = useAppSelector(state => state.approvisonnement.entities);
  const soldeTotal = useAppSelector(state => state.approvisonnement.soldeTotal);
  const loading = useAppSelector(state => state.approvisonnement.loading);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isComptable = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.COMPTABLE]));
  const isDirecteur = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.DIRECTEUR]));
  const token = Storage.local.get('jhi-authenticationToken') || Storage.session.get('jhi-authenticationToken');
  const hasAccess = isAdmin || isDirecteur || isComptable;
  const [menuVisible, setMenuVisible] = useState(false);
  const inactiveAnnee = useCheckIsAnneeInactive(props.match.params.anneeScolaireId);

  const { match } = props;

  const handleMenuClick = e => {
    setMenuVisible(false);
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item>
        <Link to={`${match.url}/transfert/new`} style={{ fontWeight: 'normal' }}>
          Depuis votre Compte KoriSchool
        </Link>
      </Menu.Item>

      <Menu.Item>
        <Link to={`${match.url}/depot/new`} style={{ fontWeight: 'normal' }}>
          Par Virement ou Dépôt Bancaire
        </Link>
      </Menu.Item>
    </Menu>
  );

  useEffect(() => {
    dispatch(getSession());
    dispatch(getEcole(props.match.params.ecoleid));
    dispatch(
      soldeTotalByEcoleSalaire({
        ecoleId: props.match.params.ecoleid,
        anneeScolaireId: props.match.params.anneeScolaireId,
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
      })
    );
    sortEntities();
  }, []);

  const renderOptionValided = id => {
    return (
      <Space size="middle">
        <Button type="primary" style={{ height: 30, width: 45 }}>
          <Link to={`${match.url}/${id}/details`}>
            <FontAwesomeIcon icon="eye" />{' '}
          </Link>
        </Button>
      </Space>
    );
  };

  const handleDownload = async id => {
    await axios({
      url: `/api/approvisonnement/${id}/file`,
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + token,
      },
      responseType: 'blob',
    })
      .then(response => {
        const url = window.URL.createObjectURL(response.data);
        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
      })
      .catch(error => console.error("Erreur lors de l'ouverture du fichier PDF :", error));
  };

  const columns = [
    {
      title: 'Date Emission',
      dataIndex: 'dateEmission',
      key: 'dateEmission',
      render: dateEmission => <TextFormat type="date" value={dateEmission} format={APP_DATE_FORMAT} />,
      align: 'center' as const,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      align: 'center' as const,
      width: 200,
      render(text, record) {
        return record.statutApprovisonnement === 'REJET' ? 'Approvisonnement par Virement/Dépôt Bancaire' : text;
      },
    },
    {
      title: 'Montant Reçu',
      dataIndex: 'montant',
      key: 'montant',
      align: 'center' as const,
    },
    {
      title: 'Statuts',
      dataIndex: 'statutApprovisonnement',
      key: 'statutApprovisonnement',
      align: 'center' as const,
      render: statut =>
        statut === 'ENCOURS' ? (
          <Tag color="red">En attente</Tag>
        ) : statut === 'VALIDE' ? (
          <Tag color="green">Validée</Tag>
        ) : statut === 'REJET' ? (
          <Tag color="#dc3545">Rejeté</Tag>
        ) : (
          <Tag color="#999">{statut}</Tag>
        ),
    },
    {
      title: 'Documents',
      key: 'documents',
      align: 'center' as const,
      width: 120,
      render(text, record) {
        return record.typeApprovisonnement === 'COMPTE_BANCAIRE' ? (
          <DownloadOutlined
            style={{ fontSize: '24px', marginRight: '8px', verticalAlign: 'middle' }}
            onClick={() => handleDownload(record.id)}
          />
        ) : (
          '-'
        );
      },
    },
    {
      title: 'Details',
      dataIndex: ['id', 'statutApprovisonnement'],
      key: 'id',
      align: 'center' as const,
      render: (text, row) => renderOptionValided(row['id']),
    },
  ];

  const dataSource = ecoleApprovisonnements
    .map((appro, i) => ({
      id: appro.id,
      dateEmission: appro.dateEmission,
      description: appro.description,
      montant: appro.montant + ' CFA',
      statutApprovisonnement: appro.statutApprovisonnement,
      typeApprovisonnement: appro.typeApprovisonnement,
    }))
    .reverse();

  const handlePagination = (currentPage: number) => {
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });
  };

  const approvisonnementList = () => {
    const entity = {
      page: paginationState.activePage - 1,
      ecoleId: props.match.params.ecoleid,
      anneeScolaireId: props.match.params.anneeScolaireId,
      size: paginationState.itemsPerPage,
    };
    dispatch(getEcoleApprovisonnements(entity));
  };

  const sortEntities = () => {
    approvisonnementList();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.push(`${props.location.pathname}`);
    }
  };

  const onSearch = vals => {
    const entity = {
      ...vals,
      ecoleId: props.match.params.ecoleid,
      anneeScolaireId: props.match.params.anneeScolaireId,
      page: paginationState.activePage - 1,
      size: paginationState.itemsPerPage,
    };
    dispatch(getEcoleApprovisonnements(entity));
  };

  const handleChange = () => {
    const entity = {
      ecoleId: props.match.params.ecoleid,
      page: paginationState.activePage - 1,
      anneeScolaireId: props.match.params.anneeScolaireId,
      size: paginationState.itemsPerPage,
    };
    dispatch(getEcoleApprovisonnements(entity));
  };

  return (
    <div>
      <h2>Historiques Approvisonnements</h2>
      <Row className="p-2 justify-content-center">
        <Row className="p-2 justify-content-center">
          {loading ? (
            <Spin tip="Chargement..." />
          ) : (
            <h4>
              {' '}
              Solde compte Salaire:{' '}
              <strong>
                <CountUp end={soldeTotal} separator="." /> FCFA
              </strong>
            </h4>
          )}
        </Row>
      </Row>

      <h2 id="classe-heading" data-cy="ClasseHeading">
        {hasAccess && (
          <div style={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }}>
            <Dropdown disabled={inactiveAnnee} overlay={menu} trigger={['click']} visible={menuVisible} onVisibleChange={setMenuVisible}>
              <Button
                style={{
                  background: inactiveAnnee ? '#ccc' : 'green',
                  color: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${inactiveAnnee ? '#ccc' : 'green'}`,
                  marginRight: '10px',
                }}
              >
                <FontAwesomeIcon icon="plus" style={{ marginRight: '8px' }} />
                Approvisionnements
              </Button>
            </Dropdown>
          </div>
        )}
      </h2>
      <Form name="basic" layout="inline" onFinish={onSearch}>
        <Row className="p-1">
          <Col span={20} sm={20} md={20} xs={17} xxl={20} xl={10} lg={24}>
            <Form.Item name="typeApprovisonnement">
              <Select placeholder="Type" allowClear onClear={handleChange}>
                <Select.Option value="COMPTE_SCOLARITE">
                  <Tag color="green">COMPTE SCOLARITE</Tag>
                </Select.Option>
                <Select.Option value="COMPTE_BANCAIRE">
                  <Tag color="gray">COMPTE BANCAIRE</Tag>
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={4} sm={7} md={7} xs={11} xxl={7} xl={10} lg={3}>
            <Form.Item name="statutApprovisonnement">
              <Select placeholder="Status" allowClear onClear={handleChange}>
                <Select.Option value="ENCOURS">
                  <Tag color="red">En attente</Tag>
                </Select.Option>
                <Select.Option value="VALIDE">
                  <Tag color="green">Validée</Tag>
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={1} sm={1} md={1} xs={1} xxl={1} xl={1} lg={1}>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div className="table-responsive">
        <Spin spinning={loading} tip="Chargement...">
          <Table dataSource={dataSource} columns={columns} rowKey="id" pagination={false} bordered />
        </Spin>
      </div>
      {totalItems ? (
        <div className={ecoleApprovisonnements && ecoleApprovisonnements.length > 0 ? 'table-responsive' : 'd-none'}>
          <Row className="justify-content-center">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </Row>
          <Row className="justify-content-center">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </Row>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Approvisonnement;
